import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import { ActionDto } from '@/Domain/api/Action/dto/Action.dto';
import { ShopifyMcpDto } from '@/Domain/api/Action/dto/ShopifyMcp.dto';
import { ActionTypeEnum } from '@/Domain/enum/ActionType.enum';
import { AppHelper } from '@/Domain/helper/App.helper';
import { UploadUrlModalHelper } from '@/Domain/helper/UploadUrlModal.helper';
import { useModalStore } from '@/Domain/store/modal.store';

type UseShopifyMcpActionFormOptions = {
  activateOnSuccess?: boolean;
  onActivateFailed?: () => void;
  onSaved?: () => void;
};

const shopifyMcpSchema = (requireValidUrl: boolean, invalidUrlMessage: string) => {
  if (requireValidUrl) {
    return z.object({
      // zod's .url() delegates to the browser URL parser, which is lenient about
      // spaces/typos; reuse the app's stricter validator (rejects whitespace, bad
      // schemes, and hosts without a TLD) so a wrong paste is caught before submit.
      shopify_url: z
        .string()
        .min(1, invalidUrlMessage)
        .refine((value) => UploadUrlModalHelper.validateUrl(AppHelper.addProtocol(value)), {
          message: invalidUrlMessage,
        }),
    });
  }
  return z.object({
    shopify_url: z.string(),
  });
};

export type ShopifyMcpForm = z.infer<typeof shopifyMcpSchema>;

export const useShopifyMcpActionForm = (
  shopifyMcpAction?: ShopifyMcpDto,
  { activateOnSuccess = false, onActivateFailed, onSaved }: UseShopifyMcpActionFormOptions = {},
) => {
  const { t } = useTranslation('shopify_mcp');
  const queryClient = useQueryClient();
  const { open, closeModal } = useModalStore();

  const activateActionMutation = useMutation<ActionDto, unknown, Partial<ActionDto>>({
    mutationKey: [QueryKey.actions.basic.patch],
  });

  const mutation = useMutation<ShopifyMcpDto, unknown, Partial<ShopifyMcpDto>>({
    mutationKey: [QueryKey.actions.shopifyMcp.update, shopifyMcpAction?.id],
    onSuccess: async (data) => {
      let saved: ShopifyMcpDto = data;
      let activationFailed = false;
      if (activateOnSuccess && !data.is_active) {
        try {
          const activated = await activateActionMutation.mutateAsync({
            id: data.id,
            is_active: true,
          });
          saved = { ...data, ...activated };
        } catch {
          // Save persisted but activation failed (e.g. tier active-action limit); keep the
          // saved URL and point the user to where they can enable it manually.
          activationFailed = true;
          toast.error(
            t('toast.activate_failed'),
            onActivateFailed
              ? { action: { label: t('toast.activate_failed_action'), onClick: onActivateFailed } }
              : undefined,
          );
        }
      }

      closeModal();

      queryClient.setQueryData([QueryKey.actions.shopifyMcp.get, saved.id], saved);
      queryClient.setQueryData<ActionDto[]>([QueryKey.actions.basic.list], (old) =>
        old?.map((action) => (action.id === saved.id ? { ...action, ...saved } : action)),
      );

      void queryClient.invalidateQueries({ queryKey: [QueryKey.actions.basic.list] });
      // Suppress the success toast when activation failed so the actionable error stands alone.
      if (!activationFailed) {
        toast.success(t('toast.update_success'));
      }

      onSaved?.();
    },
    onError: () => {
      toast.error(t('toast.update_failed'), {
        duration: 5000,
        description: t('toast.update_failed'),
      });
    },
  });

  const form = useForm<ShopifyMcpForm>({
    mode: 'onChange',
    defaultValues: {
      shopify_url: shopifyMcpAction?.shopify_url || '',
    },
    // The connect modal activates on save, so it must require a valid URL even
    // before an action exists; the Actions page keeps the inactive-draft path lax.
    resolver: zodResolver(
      shopifyMcpSchema(
        !!shopifyMcpAction?.is_active || activateOnSuccess,
        t('form.shopify_url_invalid'),
      ),
    ),
  });

  const createShopifyMCPActionMutation = useMutation<ShopifyMcpDto, unknown, void>({
    mutationKey: [QueryKey.actions.shopifyMcp.create],
  });

  const validateMcpMutation = useMutation<string[], unknown, { store_url: string }>({
    mutationKey: [QueryKey.actions.shopifyMcp.test],
  });

  const isSubmittingRef = useRef(false);

  const handleSubmit = async (data: ShopifyMcpForm) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      const normalizedUrl = data.shopify_url.replace(/\/+$/, '');

      // Validate that {url}/api/mcp is a live Shopify MCP before saving, so a typo
      // or non-MCP URL is caught here instead of silently failing at chat runtime.
      if (normalizedUrl) {
        try {
          await validateMcpMutation.mutateAsync({
            store_url: AppHelper.addProtocol(normalizedUrl),
          });
        } catch {
          form.setError('shopify_url', { message: t('form.shopify_url_not_valid_mcp') });
          toast.error(t('form.shopify_url_not_valid_mcp'));
          return;
        }
      }

      let shopifyMcpActionId = shopifyMcpAction?.id;
      if (!shopifyMcpActionId) {
        // Only one Shopify MCP action is allowed per scenario; Shopify-install agents already have one auto-provisioned, so reuse it instead of creating a duplicate (which 400s).
        let actions: ActionDto[] | undefined;
        try {
          actions = await queryClient.ensureQueryData<ActionDto[]>({
            queryKey: [QueryKey.actions.basic.list],
          });
        } catch {
          toast.error(t('toast.create_failed'));
          return;
        }
        const existing = actions?.find((action) => action.type === ActionTypeEnum.ShopifyRemoteMcp);
        shopifyMcpActionId =
          existing?.id ?? (await createShopifyMCPActionMutation.mutateAsync()).id;
      }
      mutation.mutate({
        id: shopifyMcpActionId,
        shopify_url: normalizedUrl,
        shopify_mcp_url: normalizedUrl ? `${normalizedUrl}/api/mcp` : normalizedUrl,
      });
    } catch {
      // Create failures surface via the create mutation's onError toast; swallow so they don't bubble as an uncaught rejection.
    } finally {
      isSubmittingRef.current = false;
    }
  };

  // Subscribe via formState so the dirty flag is tracked by react-hook-form's proxy.
  const { isDirty } = form.formState;

  useEffect(() => {
    // Don't clobber in-progress typing when the async prefill resolves late.
    if (isDirty) return;

    if (shopifyMcpAction) {
      form.reset({
        shopify_url: shopifyMcpAction.shopify_url,
      });

      return;
    }

    form.reset({
      shopify_url: '',
    });
  }, [open, shopifyMcpAction]);

  const onInvalid = () => {
    toast.error(t('toast.invalid_update_on_is_active'));
  };

  return {
    form,
    handleSubmit,
    onInvalid,
    isSaving: mutation.isPending || validateMcpMutation.isPending,
  };
};
